import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut, Star } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { driverService } from '../../services/supabaseService';
import ThemeToggle from '../../components/ui/ThemeToggle';
import DriverProfileHeader from './components/DriverProfileHeader';
import DriverAccountSettings from './components/DriverAccountSettings';
import DriverNotificationPreferences from './components/DriverNotificationPreferences';
import EarningsDashboard from './components/EarningsDashboard';
import DocumentsSection from './components/DocumentsSection';
import VehicleInfoEditor from './components/VehicleInfoEditor';
import PayoutSettings from './components/PayoutSettings';
import DriverDangerZone from './components/DriverDangerZone';

const DriverProfileScreen = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [documents, setDocuments] = useState({});

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const driverProfile = await driverService?.getDriverProfile(user?.id);
          setProfile(driverProfile);

          const meta = user?.user_metadata;
          if (meta?.driverNotifPrefs) setNotifPrefs(meta.driverNotifPrefs);
          if (meta?.documents) setDocuments(meta.documents);
        } else {
          navigate('/driver-login-screen');
        }
      } catch (err) {
        console.error('Failed to load driver profile:', err);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [navigate]);

  const handleUpdateProfile = async (updates) => {
    setProfile(prev => ({ ...prev, ...updates }));
  };

  const handleSaveNotifPrefs = async (prefs) => {
    const { error } = await supabase?.auth?.updateUser({ data: { driverNotifPrefs: prefs } });
    if (error) throw error;
    setNotifPrefs(prefs);
  };

  const handleUpdateVehicle = async (vehicleData) => {
    setProfile(prev => ({ ...prev, ...vehicleData }));
  };

  const handleUpdatePayout = async (payoutData) => {
    setProfile(prev => ({ ...prev, ...payoutData }));
  };

  const handleReuploadDoc = (docKey) => {
    // In a real app, this would open a file picker
    console.log('Re-upload document:', docKey);
  };

  const handleSignOut = async () => {
    await supabase?.auth?.signOut();
    navigate('/driver-login-screen');
  };

  const handleAccountDeleted = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1a365d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const earnings = {
    today: profile?.walletBalance ? Math.round(profile?.walletBalance * 0.1) : 0,
    week: profile?.walletBalance ? Math.round(profile?.walletBalance * 0.4) : 0,
    month: profile?.walletBalance ?? 0,
    pending: 0,
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
      {/* Top Nav */}
      <div className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/driver-dashboard-screen')}
            className="flex items-center gap-2 text-[#1a365d] dark:text-blue-400 font-medium hover:text-[#2d5a87] dark:hover:text-blue-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back to Dashboard</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#1a365d] dark:text-white">On</span>
            <span className="text-sm font-bold text-[#ffd700] bg-[#1a365d] dark:bg-slate-700 px-1.5 py-0.5 rounded">TheGo</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/ratings-review-screen')}
              className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-[#1a365d] dark:hover:text-blue-300 transition-colors"
              title="View Ratings & Reviews"
            >
              <Star className="w-4 h-4" />
              <span className="hidden sm:inline">Reviews</span>
            </button>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="heading-1 text-[#1a365d] dark:text-white mb-1">Driver Profile</h1>
            <p className="body-text text-slate-500 dark:text-slate-400">Manage your driver account, documents, and payout settings</p>
          </div>
          <div className="flex-shrink-0 pt-1">
            <ThemeToggle variant="pill" />
          </div>
        </div>

        <DriverProfileHeader profile={profile} onEditPhoto={() => {}} />

        <DriverAccountSettings profile={profile} onUpdate={handleUpdateProfile} />

        <DriverNotificationPreferences preferences={notifPrefs} onSave={handleSaveNotifPrefs} />

        <EarningsDashboard earnings={earnings} />

        <DocumentsSection documents={documents} onReupload={handleReuploadDoc} />

        <VehicleInfoEditor profile={profile} onUpdate={handleUpdateVehicle} />

        <PayoutSettings profile={profile} onUpdate={handleUpdatePayout} />

        <DriverDangerZone onDeleted={handleAccountDeleted} pendingEarnings={earnings?.pending} />
      </div>
    </div>
  );
};

export default DriverProfileScreen;
